import log from '@/lib/logger';
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from 'zod'
import {useUsersTranslation, useCommonTranslation} from '@/i18n/hooks';
import {Heading, Subheading} from "@/components/tailwind/heading.tsx";
import {Input} from "@/components/tailwind/input.tsx";
import {Button} from "@/components/tailwind/button.tsx";
import {Text} from "@/components/tailwind/text.tsx";
import {Divider} from "@/components/tailwind/divider.tsx";
import {Select} from "@/components/tailwind/select.tsx";
import ValidationAlert from "./validation-alert.tsx";
import {UserDTO} from "@/models/user.ts";
import {useMemo, useEffect} from "react";
import ChangePasswordDialog from "@/components/change-password-dialog.tsx";
import PasswordStrengthIndicator from "@/components/password-strength-indicator.tsx";
import {ConfirmationDialog, useConfirmationDialog} from "@/components/confirmation-dialog";
import {useAuth} from "@/context/use-auth.tsx";
import {deleteUser} from "@/services/user-services.ts";
import {useNavigate} from "react-router-dom";
import {useToast} from "@/hooks/use-toast.ts";
import {useUsernameAvailability} from "@/hooks/use-username-availability.ts";
import {usePasswordPolicy} from "@/hooks/use-password-policy.ts";
import {ProblemDetail, ValidationIssue} from "@/types/problem-details.ts";
import {formatDateTimeString} from "@/lib/utils.ts";
import {
    collectPasswordPolicyViolations,
    type PasswordPolicyViolation,
} from "@/lib/password-policy-violations.ts";

const POLICY_VIOLATION_KEYS: Record<PasswordPolicyViolation['kind'], string> = {
    'min-length': 'userForm.errors.password-min-length',
    'requires-digit': 'userForm.errors.password-requires-digit',
    'requires-special-char': 'userForm.errors.password-requires-special-char',
    'min-distinct-chars': 'userForm.errors.password-min-distinct-chars',
}

type TranslationFn = (key: string, params?: Record<string, unknown>) => string;

interface UserFormProps {
    mode: 'create' | 'edit';
    initialData?: UserDTO;
    onSubmit: (data: unknown, event?: unknown) => void | Promise<unknown>;
    isLoading?: boolean;
    loadError?: string | null;
    userId?: string;
}

function createValidationSchema(mode: 'create' | 'edit', t: TranslationFn) {
    const baseSchema = {
        name: z.string({
            error: t("userForm.errors.missing-name")
        }).min(1, t("userForm.errors.missing-name")),
        email: z.email().optional().or(z.literal("")),
        role: z.enum(["ADMIN", "USER"], {
            error: () => t("userForm.errors.invalid-role")
        }),
        active: z.coerce.boolean(),
    }

    if (mode === 'create') {
        return z.object({
            ...baseSchema,
            username: z.string({
                error: t("userForm.errors.missing-username")
            })
                .min(1, t("userForm.errors.missing-username"))
                .regex(/^[A-Za-z0-9]/, t("userForm.errors.invalid-username")),
            // Only presence is checked client-side. The minimum length and every strength rule are
            // owned by the configurable backend policy and surfaced as server-side findings, so the
            // form does not duplicate (and drift from) the policy's length here.
            password: z.string({
                error: t("userForm.errors.missing-password")
            })
                .min(1, t("userForm.errors.missing-password")),
            confirm_password: z.string({
                error: t("userForm.errors.missing-confirm-password")
            })
                .min(1, t("userForm.errors.missing-confirm-password")),
        }).refine((data) => data.password === data.confirm_password, {
            message: t("userForm.errors.password-mismatch"),
            path: ["confirm_password"]
        })
    } else {
        return z.object({
            ...baseSchema,
            id: z.string()
                .uuid(t("userForm.errors.invalid-id"))
                .min(1, t("userForm.errors.missing-id")),
        })
    }
}

export default function UserForm({mode, initialData, onSubmit, isLoading, loadError, userId}: UserFormProps) {
    const {t} = useUsersTranslation()
    const {t: tCommon} = useCommonTranslation()
    const {user, token} = useAuth()
    const navigate = useNavigate()
    const {toast} = useToast()
    
    const validationSchema = useMemo(() => createValidationSchema(mode, t), [mode, t])
    const passwordPolicy = usePasswordPolicy(token)

    const confirmationDialog = useConfirmationDialog()

    const {
        register,
        handleSubmit,
        reset,
        setError,
        formState: {errors},
        watch
    } = useForm<any>({
        resolver: zodResolver(validationSchema as any),
        defaultValues: {
            active: true,
            role: "USER"
        }
    });

    // Map a rejected submission onto the form. Backend validation issues (e.g. password policy
    // findings) become field errors; the message keys are translated with their interpolation args
    // so placeholders such as {{minLength}} render the configured value. Anything else surfaces as a
    // generic root error.
    function applyServerError(error: ProblemDetail) {
        if (error?.validationIssues?.length) {
            error.validationIssues.forEach((issue: ValidationIssue) => {
                setError(issue.field, {message: t(issue.message, issue.args)})
            })
        } else {
            setError("root", {
                type: `${error?.status ?? "unknown"}`,
                message: error?.status
                    ? tCommon("serverError.responseCode", {statusCode: error.status})
                    : tCommon("serverError.unknown"),
            })
        }
    }

    const submit = handleSubmit(async (data, event) => {
        if (mode === 'create') {
            const violations = collectPasswordPolicyViolations(
                (data as {password?: string}).password ?? '',
                passwordPolicy,
            )
            if (violations.length > 0) {
                const first = violations[0]
                setError('password', {
                    message: t(POLICY_VIOLATION_KEYS[first.kind], {
                        minLength: first.minLength,
                        minDistinctChars: first.minDistinctChars,
                    }),
                })
                return
            }
        }
        try {
            await onSubmit(data, event)
        } catch (error) {
            applyServerError(error as ProblemDetail)
        }
    })

    const isActive = watch("active", true);
    const usernameValue = (watch("username") as string | undefined) ?? "";
    const nameValue = (watch("name") as string | undefined) ?? "";
    const passwordValue = (watch("password") as string | undefined) ?? "";
    const usernameAvailability = useUsernameAvailability(
        mode === 'create' ? usernameValue : '',
        token,
    );
    // Forwarded to the strength indicator and to ChangePasswordDialog so zxcvbn-ts penalises the
    // same self-references that the server-side policy blocks (username, real name).
    const strengthUserInputs = mode === 'create'
        ? [usernameValue, nameValue].filter((value): value is string => value.length > 0)
        : [initialData?.username, initialData?.name].filter((value): value is string => Boolean(value))
    const submitDisabled = mode === 'create' &&
        (usernameAvailability.status === 'checking' || usernameAvailability.status === 'unavailable');

    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            if (mode === 'edit') {
                reset({
                    id: initialData.id,
                    name: initialData.name,
                    email: initialData.email ?? "",
                    active: initialData.active ?? true,
                    role: initialData.role as "USER" | "ADMIN",
                })
            } else {
                reset({
                    name: initialData.name,
                    username: initialData.username,
                    email: initialData.email ?? "",
                    active: initialData.active ?? true,
                    role: initialData.role,
                })
            }
        }
    }, [initialData, reset, mode])

    const handleDeleteUser = () => {
        if (!userId || !token) return;
        
        deleteUser(userId, token)
            .then(() => {
                log.warn("User deleted successfully.")
                navigate("/admin/users")
                toast({
                    title: t("userForm.dialog.toast.success.title"),
                    description: t("userForm.dialog.toast.success.description"),
                    duration: 10000,
                })
            })
            .catch((reason) => {
                log.error("Something went REALLY wrong while deleting the user", reason)
                toast({
                    title: t("userForm.dialog.toast.error.title"),
                    description: t("userForm.dialog.toast.error.description"),
                    variant: "destructive",
                    duration: 10000,
                })
            })
    }

    const openDeleteConfirmation = () => {
        confirmationDialog.openWithConfig({
            title: "deleteConfirmation.user.title",
            description: "deleteConfirmation.user.description",
            descriptionValues: {
                name: initialData?.name || '',
                username: initialData?.username || ''
            },
            cancelButton: {
                text: "deleteConfirmation.buttons.cancel.text",
                ariaLabel: "deleteConfirmation.buttons.cancel.ariaLabel"
            },
            confirmButton: {
                text: "deleteConfirmation.buttons.submit.text",
                ariaLabel: "deleteConfirmation.buttons.submit.ariaLabel",
                color: "red"
            }
        })
    }

    if (isLoading) {
        return (
            <div className="mx-auto max-w-4xl">
                <Heading>{mode === 'create' ? t("userForm.mainHeader.new") : t("userForm.mainHeader.edit")}</Heading>
                <Text>{t("userForm.loading")}</Text>
            </div>
        )
    }

    if (loadError) {
        return (
            <div className="mx-auto max-w-4xl">
                <Heading>{mode === 'create' ? t("userForm.mainHeader.new") : t("userForm.mainHeader.edit")}</Heading>
                <ValidationAlert message={loadError} />
            </div>
        )
    }

    return (
        <>
            <form onSubmit={submit} className="mx-auto max-w-4xl">
                <Heading>{mode === 'create' ? t("userForm.mainHeader.new") : t("userForm.mainHeader.edit")}</Heading>

                <Divider className="my-10 mt-6" />

                {mode === 'edit' && (
                    <Input
                        id="id"
                        type="hidden"
                        {...register("id")}
                    />
                )}

                {(errors.root || (mode === 'edit' && errors.id)) && (
                    <section aria-label={t("userForm.errors.section.ariaLabel")}>
                        {errors.root && (
                            <ValidationAlert message={t(String(errors.root.message) || "unknown-server-issue")}/>
                        )}
                        {mode === 'edit' && errors.id && (
                            <ValidationAlert message={t(String(errors.id.message) || "unknown-id-issue")}/>
                        )}
                    </section>
                )}

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Subheading>{t("userForm.fields.name.subheading")}</Subheading>
                        <Text>{t("userForm.fields.name.text")}</Text>
                    </div>
                    <div>
                        <Input aria-label={t("userForm.fields.name.text")} type="text" {...register("name")}
                               placeholder={t("userForm.fields.name.placeholder")} invalid={!!errors.name}
                               autoComplete="name" data-testid="user-form-name" />
                        {errors.name &&
                            <ValidationAlert message={t(String(errors.name.message) || 'userForm.errors.unknown-name-issue')}/>}
                    </div>
                </section>

                <Divider className="my-10" soft/>

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Subheading id="usernameHeadline">{t('userForm.fields.username.subheading')}</Subheading>
                        <Text id="usernameDescriptions">{t('userForm.fields.username.text')}</Text>
                    </div>

                    {mode === 'create' ? (
                        <div>
                            <Input aria-label={t("userForm.fields.username.text")} type="text"
                                   autoComplete="username" {...register("username")}
                                   placeholder={t("userForm.fields.username.placeholder")}
                                   invalid={!!errors.username || usernameAvailability.status === 'unavailable'}
                                   data-testid="user-form-username"/>
                            {errors.username &&
                                <ValidationAlert
                                    message={t(String(errors.username.message) || 'userForm.errors.unknown-username-issue')}/>}
                            <p
                                id="username-availability-status"
                                role="status"
                                aria-live="polite"
                                data-testid="username-availability-status"
                                data-status={usernameAvailability.status}
                                className={
                                    usernameAvailability.status === 'available'
                                        ? "mt-2 text-sm text-green-700 dark:text-green-300"
                                        : usernameAvailability.status === 'unavailable'
                                            ? "mt-2 text-sm text-red-700 dark:text-red-300"
                                            : usernameAvailability.status === 'error'
                                                ? "mt-2 text-sm text-amber-700 dark:text-amber-300"
                                                : usernameAvailability.status === 'checking'
                                                    ? "mt-2 text-sm text-zinc-500 dark:text-zinc-400"
                                                    : "sr-only"
                                }
                            >
                                {usernameAvailability.status === 'checking' &&
                                    t("userForm.fields.username.availability.checking")}
                                {usernameAvailability.status === 'available' &&
                                    t("userForm.fields.username.availability.available")}
                                {usernameAvailability.status === 'unavailable' &&
                                    t("userForm.fields.username.availability.unavailable")}
                                {usernameAvailability.status === 'error' &&
                                    t("userForm.fields.username.availability.error")}
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col justify-center">
                            <Text aria-labelledby="usernameHeadline usernameDescriptions" className="font-medium">
                                {initialData?.username}
                            </Text>
                        </div>
                    )}
                </section>

                <Divider className="my-10" soft/>

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Subheading>{t("userForm.fields.email.subheading")}</Subheading>
                        <Text>{t("userForm.fields.email.text")}</Text>
                    </div>
                    <div>
                        <Input aria-label={t("userForm.fields.email.text")} type="email" {...register("email")}
                               placeholder={t("userForm.fields.email.placeholder")}
                               autoComplete="email" data-testid="user-form-email" />
                    </div>
                </section>

                {mode === 'create' && (
                    <>
                        <Divider className="my-10" soft/>

                        <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                            <div className="space-y-1">
                                <Subheading id="password">{t('userForm.fields.password.subheading')}</Subheading>
                                <Text>{t('userForm.fields.password.text')}<br/><sup>{t("userForm.fields.password.additionalText", {minLength: passwordPolicy.minLength})}</sup></Text>
                            </div>
                            <div>
                                <Input aria-label={t("userForm.fields.password.text")} type="password"
                                       autoComplete="new-password" {...register("password")} placeholder="*****"
                                       invalid={!!errors.password} data-testid="user-form-password"/>
                                <PasswordStrengthIndicator
                                    password={passwordValue}
                                    userInputs={strengthUserInputs}
                                    minScore={passwordPolicy.minStrengthScore}
                                />
                                {errors.password &&
                                    <ValidationAlert
                                        message={t(String(errors.password.message) || 'userForm.errors.unknown-password-issue')}/>}
                            </div>
                        </section>

                        <Divider className="my-10" soft/>

                        <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                            <div className="space-y-1">
                                <Subheading id="confirmPassword">{t('userForm.fields.confirmPassword.subheading')}</Subheading>
                                <Text>{t('userForm.fields.confirmPassword.text')}</Text>
                            </div>
                            <div>
                                <Input aria-label={t("userForm.fields.confirmPassword.text")} autoComplete="new-password"
                                       type="password" {...register("confirm_password")} placeholder="*****"
                                       invalid={!!errors.confirm_password} data-testid="user-form-confirm-password"/>
                                {errors.confirm_password &&
                                    <ValidationAlert
                                            message={t(String(errors.confirm_password.message) || "userForm.errors.unknown-confirm-password-issue")}/>}
                            </div>
                        </section>
                    </>
                )}

                <Divider className="my-10" soft/>

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Subheading>{t('userForm.fields.role.subheading')}</Subheading>
                        <Text>{t('userForm.fields.role.text')}</Text>
                    </div>
                    <div>
                        <Select aria-label={t("userForm.fields.role.text")} {...register("role")} invalid={!!errors.role} data-testid="role-select">
                            {["USER", "ADMIN"].map((role) => (
                                <option key={role} value={role}>{t(`userForm.fields.role.options.${role}`)}</option>
                            ))}
                        </Select>
                        {errors.role &&
                            <ValidationAlert message={t(String(errors.role.message) || 'unknown-role-issue')}/>}
                    </div>
                </section>

                <Divider className="my-10" soft/>

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Subheading id="active-headline">{t('userForm.fields.active.subheading')}</Subheading>
                        <Text id="active-description">{t('userForm.fields.active.text')}</Text>
                    </div>
                    <div>
                        <div className="flex items-center space-x-2">
                            <input type={"checkbox"}
                                   {...register("active")}
                                   aria-labelledby="active-headline active-description"
                                   data-testid="active-checkbox"
                            />
                            <Text>({t("userForm.fields.active.checkbox." + (isActive ? "active" : "inactive"))})</Text>
                        </div>
                        {errors.active &&
                            <ValidationAlert message={t(String(errors.active.message) || "unknown-active-issue")}/>}
                    </div>
                </section>

                <Divider className="my-10" soft/>

                {mode === 'create' ? (
                    <section className="flex justify-end gap-4">
                        <div className="space-x-1.5">
                            <Button aria-label={tCommon("buttons.cancel.ariaLabel")} href="/admin/users">{tCommon('buttons.cancel.text')}</Button>
                            <Button aria-label={tCommon("buttons.submit.ariaLabel")} type="submit"
                                    disabled={submitDisabled}
                                    data-testid="submit-user-form">{tCommon('buttons.submit.text')}</Button>
                        </div>
                    </section>
                ) : null}

            </form>

            {mode === 'edit' && initialData && (
                <>
                    <div id="password-change-section" className="mx-auto max-w-4xl">
                        <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                            <div className="space-y-1">
                                <Subheading id="change-password-headline">
                                    {t('userForm.buttons.changePassword.subheading')}
                                </Subheading>
                                <Text id="change-password-description">
                                    {t('userForm.buttons.changePassword.description')}
                                </Text>
                            </div>
                            <div className="flex flex-col items-center justify-center gap-2">
                                <ChangePasswordDialog
                                    effectedUserId={initialData.id}
                                    userInputs={strengthUserInputs}
                                />

                                <Text data-testid="user-form-password-changed-at">
                                    {t('userForm.buttons.changePassword.lastChanged')}:{' '}
                                    {initialData.passwordChangedAt
                                        ? formatDateTimeString(initialData.passwordChangedAt)
                                        : t('userForm.buttons.changePassword.lastChangedNever')}
                                </Text>
                            </div>
                        </section>
                    </div>

                    <form onSubmit={submit} className="mx-auto max-w-4xl">
                        <Divider className="my-10" soft/>

                        <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                            <div>
                                {user?.userName !== initialData.username && (
                                    <Button
                                        type="button"
                                        onClick={openDeleteConfirmation}
                                        aria-label={t("userForm.buttons.delete.ariaLabel")}
                                        data-testid="delete-user-button">
                                        {t('userForm.buttons.delete.text')}
                                    </Button>
                                )}
                            </div>
                            <div className="flex justify-end gap-4">
                                <Button aria-label={tCommon("buttons.cancel.ariaLabel")}
                                        href="/admin/users">
                                    {tCommon('buttons.cancel.text')}
                                </Button>
                                <Button aria-label={tCommon("buttons.submit.ariaLabel")}
                                        type="submit"
                                        data-testid="submit-user-form">
                                    {tCommon('buttons.submit.text')}
                                </Button>
                            </div>
                        </section>
                    </form>
                </>
            )}
            
            {confirmationDialog.dialogProps && (
                <ConfirmationDialog
                    {...confirmationDialog.dialogProps}
                    onConfirm={handleDeleteUser}
                    namespace="common"
                />
            )}
        </>
    )
}